import React, { FC } from 'react'
import { HStack } from '@chakra-ui/react'

import { WeightUnit, lbToStLb, stLbToLb } from '@gaia-family/lib'

import { NumberInput } from '../Number'
import { InputProps } from '../Input'
import { RadioGroup } from '../Radio'

export type WeightProps = {
  value: Gaia.Weight | null
  onChange: (weight: Gaia.Weight | null) => void
  isInvalid?: boolean
  name?: string
  dataCy?: string
  defaultUnit?: WeightUnit
  unitOptions?: WeightUnit[]
  setTouched?: (value: boolean, shouldValidate?: boolean | undefined) => void
} & Pick<InputProps, 'variant' | 'onBlur' | 'onFocus'>

const allWeightOptions = [
  { name: 'kg', value: WeightUnit.Kilograms },
  { name: 'st/lb', value: WeightUnit.PoundsStones },
  { name: 'lb', value: WeightUnit.Pounds },
]

function getWeightOptions(unitOptions: WeightUnit[]) {
  return allWeightOptions.filter((option) =>
    unitOptions.some((receivedOption) => receivedOption === option.value)
  )
}

export const WeightInput: FC<WeightProps> = ({
  variant,
  onChange,
  value: weight,
  isInvalid,
  onBlur,
  onFocus,
  name,
  dataCy,
  defaultUnit = WeightUnit.Kilograms,
  unitOptions = [WeightUnit.Kilograms, WeightUnit.PoundsStones],
  setTouched,
}) => {
  const weightOptions = getWeightOptions(unitOptions)

  const toggleUnit = (newUnit: WeightUnit) => {
    onChange({
      unit: newUnit,
      value: 0,
    })
    setTouched?.(false)
  }

  const handleStonesChange = (value: number | null = 0) => {
    const cur = lbToStLb(weight?.value ? weight?.value : 0)
    onChange({
      unit: WeightUnit.PoundsStones,
      value: Math.round(stLbToLb(value ?? 0, cur[1])),
    })
  }

  const handlePoundsRemainderChange = (value: number | null = 0) => {
    const cur = lbToStLb(weight?.value ? weight?.value : 0)
    onChange({
      unit: WeightUnit.PoundsStones,
      value: Math.round(stLbToLb(cur[0], value ?? 0)),
    })
  }

  const renderWeightInput = (unit: WeightUnit) => {
    switch (unit) {
      case WeightUnit.PoundsStones:
        return (
          <>
            <NumberInput
              key="st"
              placeholder="st"
              isInvalid={isInvalid}
              onChange={handleStonesChange}
              value={weight?.value ? lbToStLb(weight.value)[0] : null}
              variant={variant}
              onFocus={onFocus}
              onBlur={onBlur}
              name={name}
              isNatural={true}
            />
            <NumberInput
              key="lb"
              placeholder="lb"
              isInvalid={isInvalid}
              onChange={handlePoundsRemainderChange}
              value={weight?.value ? lbToStLb(weight.value)[1] : null}
              variant={variant}
              onFocus={onFocus}
              onBlur={onBlur}
              name={name}
              isNatural={true}
            />
          </>
        )

      case WeightUnit.Pounds:
        return (
          <NumberInput
            key="lb"
            placeholder="lb"
            isInvalid={isInvalid}
            onChange={(value) =>
              onChange({
                unit: WeightUnit.Pounds,
                value: Math.round(value ?? 0),
              })
            }
            value={weight?.value}
            variant={variant}
            onFocus={onFocus}
            onBlur={onBlur}
            name={name}
            isNatural={true}
          />
        )
      default:
        return (
          <NumberInput
            key="kg"
            placeholder="kg"
            isInvalid={isInvalid}
            onChange={(value) =>
              onChange({
                unit: WeightUnit.Kilograms,
                value: Math.round(value ?? 0),
              })
            }
            value={weight?.value ?? null}
            variant={variant}
            onFocus={onFocus}
            onBlur={onBlur}
            name={name}
          />
        )
    }
  }

  const unit = weight?.unit ?? defaultUnit

  return (
    <HStack spacing="sm" data-cy={dataCy} alignItems="left" width="100%">
      <HStack spacing="sm" direction="row" flex={1}>
        {renderWeightInput(unit)}
      </HStack>
      <RadioGroup
        options={weightOptions}
        value={unit}
        name="unit"
        onChange={toggleUnit}
        width="fit-content"
      />
    </HStack>
  )
}
